<!-- src/components/forms/completed/FatigueRiskAssessmentCompleted.vue -->
<template>
  <div class="space-y-16 animate-fadeIn">
    
    <!-- 1. RECORDING CONTEXT -->
    <section>
      <div class="flex items-center gap-4 mb-8">
        <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
          <ClipboardDocumentIcon class="h-6 w-6" />
        </div>
        <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">1. Recording Context</h2>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-8 bg-slate-50 p-8 rounded-[40px] border border-slate-100 shadow-inner">
        <div class="StatCard bg-white">
          <span class="label">Recording Date</span>
          <span class="value">{{ formatDate(flattenedData.recordingDate, 'dateOnly') }}</span>
        </div>
        <div class="StatCard bg-white">
          <span class="label">Recording Time</span>
          <span class="value">{{ flattenedData.recordingTime || 'N/A' }}</span>
        </div>
        <div class="StatCard bg-white">
          <span class="label">Requested By</span>
          <span class="value uppercase">{{ flattenedData.requestedBy || 'N/A' }}</span>
        </div>
      </div>
    </section>

    <!-- 2. TYPE OF EXCEEDANCE -->
    <section>
      <div class="flex items-center gap-4 mb-8">
        <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
          <ClockIcon class="h-6 w-6" />
        </div>
        <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">2. Type of Exceedance</h2>
      </div>

      <div class="space-y-8">
        <!-- Exceedance Types Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div v-for="type in exceedanceTypes" :key="type" class="flex items-center p-4 bg-white border border-slate-200 rounded-2xl">
            <CheckCircleIcon class="h-5 w-5 text-[#20FCF1] shrink-0" />
            <span class="ml-4 text-[11px] font-black text-slate-700 uppercase tracking-tight">{{ type }}</span>
          </div>
        </div>

        <!-- Affected Employees -->
        <div class="bg-white p-8 rounded-[32px] border border-slate-200 shadow-xl">
          <p class="micro-label text-slate-400 mb-4">Personnel Affected by Exceedance</p>
          <div class="flex flex-wrap gap-3">
            <span v-for="emp in flattenedData.affectedEmployees" :key="emp.id" class="px-4 py-2 bg-slate-100 rounded-xl text-xs font-black text-[#0A1630] uppercase border border-slate-200">
              {{ emp.name }}
            </span>
          </div>
          <div class="mt-6 pt-6 border-t border-slate-100 flex items-center gap-3">
             <div :class="flattenedData.employeesConsulted ? 'bg-[#20FCF1]' : 'bg-slate-200'" class="p-1 rounded-full">
               <CheckIcon class="h-3 w-3 text-white" />
             </div>
             <p class="text-[10px] font-black uppercase tracking-widest text-slate-500">Employees have been formally consulted on the exceedance</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 3. CIRCUMSTANCES & OPTIONS -->
    <section>
      <div class="flex items-center gap-4 mb-8">
        <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
          <MagnifyingGlassIcon class="h-6 w-6" />
        </div>
        <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">3. Circumstances & Options</h2>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div class="space-y-6">
          <div class="bg-white p-8 rounded-[32px] border border-slate-200 shadow-sm min-h-[150px]">
            <p class="micro-label text-slate-400 mb-2">Terms of Exceedance</p>
            <p class="text-sm text-slate-700 leading-relaxed italic">{{ flattenedData.termsOfExceedance || 'No specific terms recorded.' }}</p>
          </div>
          <div class="bg-slate-50 p-6 rounded-3xl border border-slate-100">
            <p class="micro-label text-[#20FCF1] mb-4">Exceptional Drivers</p>
            <div class="space-y-2">
              <div v-for="opt in flattenedData.exceptionalDrivers" :key="opt" class="flex items-center gap-2 text-[10px] font-bold text-slate-600 uppercase">
                <div class="w-1 h-1 rounded-full bg-[#20FCF1]"></div> {{ opt }}
              </div>
            </div>
          </div>
        </div>

        <div class="space-y-6">
          <div class="bg-slate-50 p-6 rounded-3xl border border-slate-100">
            <p class="micro-label text-[#20FCF1] mb-4">Alternative Options Considered</p>
            <div class="space-y-2">
              <div v-for="opt in flattenedData.alternativesConsidered" :key="opt" class="flex items-center gap-2 text-[10px] font-bold text-slate-600 uppercase">
                <div class="w-1 h-1 rounded-full bg-[#20FCF1]"></div> {{ opt }}
              </div>
            </div>
          </div>
          <div class="bg-slate-50 p-6 rounded-3xl border border-slate-100">
            <p class="micro-label text-[#20FCF1] mb-4">Reason Relief Could Not Be Provided</p>
            <div class="grid grid-cols-2 gap-2">
              <div v-for="opt in flattenedData.reliefFailReasons" :key="opt" class="flex items-center gap-2 text-[10px] font-bold text-slate-600 uppercase">
                <div class="w-1 h-1 rounded-full bg-rose-400"></div> {{ opt }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 4. HAZARD ASSESSMENT & CONTROLS -->
    <section>
      <div class="flex items-center gap-4 mb-8">
        <div class="bg-rose-500 text-white w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
          <ShieldExclamationIcon class="h-6 w-6" />
        </div>
        <h2 class="text-2xl font-black text-rose-600 uppercase tracking-tight">4. Critical Hazard Assessment</h2>
      </div>

      <div class="bg-slate-900 p-10 rounded-[40px] shadow-2xl relative overflow-hidden">
        <div class="absolute -right-10 -bottom-10 opacity-5"><ShieldExclamationIcon class="w-64 h-64" /></div>
        <p class="micro-label text-[#20FCF1] mb-6">Identified Risks for this Exceedance</p>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div v-for="h in flattenedData.identifiedHazards" :key="h" class="flex items-center p-4 bg-white/5 border border-white/10 rounded-2xl font-black text-[10px] text-slate-300 uppercase tracking-widest">
            {{ h }}
          </div>
        </div>
        <div v-if="flattenedData.otherHazardsDetail" class="mt-10 pt-6 border-t border-white/10">
          <p class="micro-label text-slate-500 mb-2">Detailed Hazards / Weather Conditions</p>
          <p class="text-sm text-white italic">{{ flattenedData.otherHazardsDetail }}</p>
        </div>
      </div>

      <div class="p-10 bg-[#EBFDF8] border-2 border-[#20FCF1]/20 rounded-[40px] shadow-xl">
        <p class="micro-label text-[#20FCF1] mb-4">Final Control Measures Implemented</p>
        <p class="text-lg font-bold text-[#0A1630] leading-relaxed">{{ flattenedData.controlMeasures || 'N/A' }}</p>
      </div>
    </section>

    <!-- 5. AUTHORIZATION SEALS -->
    <section class="border-t pt-12">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-12">
        <!-- Manager Seal -->
        <div v-if="flattenedData.managerSignature" class="bg-white p-8 rounded-[40px] border border-slate-200 shadow-xl flex flex-col items-center">
          <p class="micro-label text-slate-400 mb-6">Line Manager / On-Call Manager</p>
          <img :src="flattenedData.managerSignature" alt="Manager Seal" class="max-h-24 object-contain grayscale brightness-75">
          <div class="w-full h-px bg-slate-100 my-6"></div>
          <p class="text-[10px] font-black text-[#0A1630] uppercase tracking-widest">{{ flattenedData.managerName || 'Authorized Assessor' }}</p>
          <p v-if="flattenedData.managerComments" class="mt-4 text-[10px] text-slate-400 italic text-center px-4">"{{ flattenedData.managerComments }}"</p>
        </div>

        <!-- Client Seal -->
        <div v-if="flattenedData.clientSignature" class="bg-[#EBFDF8] p-8 rounded-[40px] border border-[#20FCF1]/30 shadow-xl flex flex-col items-center">
          <p class="micro-label text-[#20FCF1] mb-6">Client Manager Approval Seal</p>
          <img :src="flattenedData.clientSignature" alt="Client Seal" class="max-h-24 object-contain">
          <div class="w-full h-px bg-[#20FCF1]/10 my-6"></div>
          <p class="text-[10px] font-black text-[#0A1630] uppercase tracking-widest">Formal Approval Verified</p>
          <p v-if="flattenedData.clientComments" class="mt-4 text-[10px] text-slate-500 italic text-center px-4">"{{ flattenedData.clientComments }}"</p>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { 
  ClipboardDocumentIcon, ClockIcon, MagnifyingGlassIcon, 
  ShieldExclamationIcon, CheckCircleIcon, CheckIcon 
} from '@heroicons/vue/24/outline';

const props = defineProps({
  submissionData: { type: Object, required: true },
  formatDate: { type: Function, required: true },
  formatText: { type: Function, required: true }
});

const flattenedData = computed(() => {
  const s = props.submissionData;
  const f = s.formData || {};
  return {
    recordingDate: s.recordingDate || f.recordingDate || null,
    recordingTime: s.recordingTime || f.recordingTime || null,
    requestedBy: s.requestedBy || f.requestedBy || 'N/A',
    selectedExceedances: s.selectedExceedances || f.selectedExceedances || [],
    affectedEmployees: s.affectedEmployees || f.affectedEmployees || [],
    employeesConsulted: s.employeesConsulted || f.employeesConsulted || false,
    termsOfExceedance: s.termsOfExceedance || f.termsOfExceedance || null,
    exceptionalDrivers: s.exceptionalDrivers || f.exceptionalDrivers || [],
    alternativesConsidered: s.alternativesConsidered || f.alternativesConsidered || [],
    reliefFailReasons: s.reliefFailReasons || f.reliefFailReasons || [],
    identifiedHazards: s.identifiedHazards || f.identifiedHazards || [],
    otherHazardsDetail: s.otherHazardsDetail || f.otherHazardsDetail || null,
    controlMeasures: s.controlMeasures || f.controlMeasures || null,
    managerSignature: s.managerSignature || f.managerSignature || null,
    managerName: s.managerName || f.managerName || null,
    managerComments: s.managerComments || f.managerComments || null,
    clientSignature: s.clientSignature || f.clientSignature || null,
    clientComments: s.clientComments || f.clientComments || null
  };
});

const exceedanceTypes = computed(() => flattenedData.value.selectedExceedances);
</script>

<style scoped>
.micro-label { @apply text-[10px] font-black uppercase tracking-[0.2em]; }
.StatCard { @apply flex flex-col p-6 rounded-[28px] border border-slate-100 shadow-sm transition-all; }
.StatCard .label { @apply text-[9px] font-black uppercase text-slate-400 tracking-widest mb-1; }
.StatCard .value { @apply text-sm font-black truncate text-[#0A1630]; }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>